import { useEffect, useState } from "react";
import { ChartData } from 'chart.js';

import ContentsProps from "../../interfaceTypes/ContentsProps";
import Dashboard from "./Dashboard";
import { base64Decoding, percentValue, useInterval } from "../../common/util";
import { PAGE_URL, PLATFORM_URL } from "../../value/Url";
import { requests } from "../../common/RestAPI";
import { SESSIONSTORAGE } from "../../value/staticValues";



export interface CountData {
    totalCount: number;
};

export interface ActiveCountData {
    totalCount: number;
    activeCount: number;
};

export interface HostClusterData {
    totalCount: number;
    computeCount: number;
    gpuCount: number;
    memoryCount: number;
    normalCount: number;
};

export interface HostData {
    totalCount: number;
    activeCount: number;
    cpuTotalCount: number;
    cpuUsedCount: number;
    memoryTotalCapacityMb: number;
    memoryUsedCapacityMb: number;
    gpuTotalCount: number;
    gpuUsedCount: number;
};

export interface StorageData {
    totalCount: number;
    activeCount: number;
    totalCapacityGb: number;
    usedCapacityGb: number;
    osdTotalCount: number;
    osdActiveCount: number;
};

export interface NodeData {
    totalCount: number;
    activeCount: number;
    cpuTotalCount: number;
    cpuUsedCount: number;
    memoryTotalCapacityMb: number;
    memoryUsedCapacityMb: number;
    storageTotalCapacityGb: number;
    storageUsedCapacityGb: number;
};

export interface PvcData{
    totalCount: number;
    activeCount: number;
    volumeTotalSizeGb: number;
};

export interface ServiceData{
    totalCount: number;
    clusterIpCount: number;
    nodePortCount: number;
    loadBalancerCount: number;
};

export interface NetworkData {
    totalCount: number;
    activeCount: number;
    externalCount: number;
};

export interface FloatingIpData {
    totalCount: number;
    activeCount: number;
    attachedCount: number;
};

export interface VmImageData {
    totalCount: number;
    activeCount: number;
};

export interface VolumeData {
    totalCount: number;
    availableCount: number;
    attachedCount: number;
    volumeTotalSizeGb: number;
};

export interface VolumeBackupData {
    totalCount: number;
};


export interface VmData {
    totalCount: number;
    activeCount: number;
    cpuTotalCount: number;
    memoryTotalCapacityGb: number;
    gpuTotalCount: number;
};

export interface MaxCountData{
    maxCount: number;
    totalCount: number;
};

export interface MaxActiveCountData {
    maxCount: number;
    totalCount: number;
    activeCount: number;
};

export interface MaxPvcData extends PvcData{
    maxCount: number;
};

export interface MaxServiceData extends ServiceData{
    maxCount: number;
};

export interface MaxNetworkData extends NetworkData{
    maxCount: number;
};

export interface MaxFloatingIpData extends FloatingIpData {
    maxCount: number;
};

export interface MaxVmImageData extends VmImageData {
    maxCount: number;
};

export interface MaxVmData {
    maxCount: number;
    totalCount: number;
    activeCount: number;
    cpuMaxCount: number;
    cpuTotalCount: number;
    memoryMaxCapacityGb: number;
    memoryTotalCapacityGb: number;
    gpuMaxCount: number;
    gpuTotalCount: number;
};

export interface MaxVolumeData extends VolumeData {
    maxCount: number;
    volumeMaxSizeGb: number;
};

export interface MaxVolumeBackupData extends VolumeBackupData {
    maxCount: number;
};


export interface DashboardInfra {
    user: ActiveCountData;
    admin: ActiveCountData;
    project: ActiveCountData;
    region: CountData;
    az: ActiveCountData;
    hostCluster: HostClusterData;
    host: HostData;
    storage: StorageData;
    containerClusterTemplate: CountData;
    containerCluster: ActiveCountData;
    node: NodeData;
    registry: CountData;
    namespace: CountData;
    containerImage: CountData;
    podProfile: CountData;
    deployment: ActiveCountData;
    pod: ActiveCountData;
    pvc: PvcData;
    service: ServiceData;
    ingress: CountData;
    network: NetworkData;
    subnet: CountData;
    securityGroup: CountData;
    securityGroupRule: CountData;
    floatingIp: FloatingIpData;
    vmImage: VmImageData;
    vmType: CountData;
    vm: ActiveCountData;
    volume: VolumeData;
    volumeBackup: VolumeBackupData;
    keyPair: CountData;
};



export interface DashboardContainer {
    containerClusterTemplate: CountData;
    containerCluster: ActiveCountData;
    node: NodeData;
    registry: CountData;
    namespace: CountData;
    containerImage: CountData;
    podProfile: CountData;
    deployment: ActiveCountData;
    pod: ActiveCountData;
    pvc: PvcData;
    service: ServiceData;
    ingress: CountData;
};

export interface DashboardProject {
    user: MaxActiveCountData;
    network: MaxNetworkData;
    subnet: MaxCountData;
    securityGroup: MaxCountData;
    securityGroupRule: MaxCountData;
    floatingIp: MaxFloatingIpData;
    vmImage: MaxVmImageData;
    vm: MaxVmData;
    volume: MaxVolumeData;
    volumeBackup: MaxVolumeBackupData;
    keyPair: MaxCountData;
};

export default function DashboardContainer(props:ContentsProps) {


    const [dashboardInfra, setDashboardInfra] = useState<DashboardInfra>({
        user: {totalCount: 0, activeCount: 0}, admin: {totalCount: 0, activeCount: 0}, project: {totalCount: 0, activeCount: 0},
        region: {totalCount: 0},
        az: {totalCount: 0, activeCount: 0},
        hostCluster: {totalCount: 0, computeCount: 0, gpuCount: 0, memoryCount: 0, normalCount: 0},
        host: {totalCount: 0, activeCount: 0, cpuTotalCount: 0, cpuUsedCount: 0, memoryTotalCapacityMb: 0, memoryUsedCapacityMb: 0, gpuTotalCount: 0, gpuUsedCount: 0},
        storage: {totalCount: 0, activeCount: 0, totalCapacityGb: 0, usedCapacityGb: 0, osdActiveCount: 0, osdTotalCount: 0},
        containerClusterTemplate: {totalCount: 0}, containerCluster: {totalCount: 0, activeCount: 0},
        node: {totalCount:0, activeCount: 0, cpuTotalCount: 0, cpuUsedCount: 0, memoryTotalCapacityMb: 0, memoryUsedCapacityMb: 0, storageTotalCapacityGb: 0, storageUsedCapacityGb: 0},
        registry: {totalCount: 0}, namespace: {totalCount: 0}, containerImage: {totalCount: 0}, podProfile:{totalCount:0},
        deployment: {totalCount: 0, activeCount: 0}, pod: {totalCount: 0, activeCount: 0}, pvc:{totalCount: 0, activeCount: 0, volumeTotalSizeGb: 0},
        service:{totalCount: 0, clusterIpCount: 0, nodePortCount:0, loadBalancerCount: 0}, ingress:{totalCount: 0},
        network:{totalCount: 0, activeCount: 0, externalCount: 0}, subnet: {totalCount: 0}, securityGroup: {totalCount: 0}, securityGroupRule: {totalCount: 0},
        floatingIp:{totalCount: 0, activeCount: 0, attachedCount: 0}, vmImage: {totalCount: 0, activeCount: 0},
        vmType: {totalCount: 0}, vm: {totalCount: 0, activeCount: 0}, volume: {totalCount: 0, availableCount: 0, attachedCount: 0, volumeTotalSizeGb: 0},
        volumeBackup: {totalCount: 0}, keyPair: {totalCount: 0}
    });
    const [dashboardContainer, setDashboardContainer] = useState<DashboardContainer>({
        containerClusterTemplate: {totalCount: 0}, containerCluster: {totalCount: 0, activeCount: 0},
        node: {totalCount:0, activeCount: 0, cpuTotalCount: 0, cpuUsedCount: 0, memoryTotalCapacityMb: 0, memoryUsedCapacityMb: 0, storageTotalCapacityGb: 0, storageUsedCapacityGb: 0},
        registry: {totalCount: 0}, namespace: {totalCount: 0}, containerImage: {totalCount: 0}, podProfile:{totalCount:0},
        deployment: {totalCount: 0, activeCount: 0}, pod: {totalCount: 0, activeCount: 0}, pvc:{totalCount: 0, activeCount: 0, volumeTotalSizeGb: 0},
        service:{totalCount: 0, clusterIpCount: 0, nodePortCount:0, loadBalancerCount: 0}, ingress:{totalCount: 0}
    });
    const [dashboardProject, setDashboardProject] = useState<DashboardProject>({
        user: {maxCount:0, totalCount: 0, activeCount: 0},
        network:{maxCount:0, totalCount: 0, activeCount: 0, externalCount: 0}, subnet: {maxCount:0, totalCount: 0}, securityGroup: {maxCount:0,totalCount: 0}, securityGroupRule: {maxCount:0, totalCount: 0},
        floatingIp:{maxCount:0, totalCount: 0, activeCount: 0, attachedCount: 0}, vmImage: {maxCount:0, totalCount: 0, activeCount: 0},
        vm: {maxCount:0, totalCount: 0, activeCount: 0, cpuMaxCount: 0, memoryMaxCapacityGb: 0, gpuMaxCount: 0, cpuTotalCount:0, memoryTotalCapacityGb: 0, gpuTotalCount: 0}, volume: {maxCount: 0, totalCount: 0, availableCount: 0, attachedCount: 0, volumeMaxSizeGb: 0, volumeTotalSizeGb: 0},
        volumeBackup: {maxCount: 0, totalCount: 0}, keyPair: {maxCount:0, totalCount: 0}
    });

    const [viewType, setViewType] = useState('');

    useEffect(() => {
        if (!viewType) {
            const userRole = base64Decoding(
                sessionStorage.getItem(SESSIONSTORAGE.USER_ROLE)
            );

            setViewType(userRole === 'ADM' ? 'Infra View' : 'Project View');
        }
    }, [])


    function parseDashboardInfra(auth, infra, virt, cont) {

        setDashboardInfra({
            // auth
            user: {
                totalCount: auth.user.user_total_count,
                activeCount: auth.user.user_active_count,
            },
            admin: {
                totalCount: auth.admin.admin_total_count,
                activeCount: auth.admin.admin_active_count
            },
            project: {
                totalCount: auth.project.project_total_count,
                activeCount: auth.project.project_active_count
            },
            // infra
            region: {
                totalCount: infra.region.region_total_count
            },
            az: {
                totalCount: infra.availability_zone.availability_zone_total_count,
                activeCount: infra.availability_zone.availability_zone_active_count
            },
            hostCluster: {
                totalCount: infra.host_cluster.host_cluster_total_count,
                computeCount: infra.host_cluster.host_cluster_type_compute_count,
                gpuCount: infra.host_cluster.host_cluster_type_gpu_count,
                memoryCount: infra.host_cluster.host_cluster_type_memory_count,
                normalCount: infra.host_cluster.host_cluster_type_normal_count
            },
            host: {
                totalCount: infra.host.host_total_count,
                activeCount: infra.host.host_active_count,
                cpuTotalCount: infra.host.vcpu_total_count,
                cpuUsedCount: infra.host.vcpu_used_count,
                memoryTotalCapacityMb: infra.host.memory_total_capacity_mb,
                memoryUsedCapacityMb: infra.host.memory_used_capacity_mb,
                gpuTotalCount: infra.host.gpu_total_count,
                gpuUsedCount: infra.host.gpu_used_count
            },
            storage: {
                totalCount: infra.storage_pool.storage_pool_total_count,
                activeCount: infra.storage_pool.storage_pool_active_count,
                totalCapacityGb: infra.storage_pool.storage_pool_total_capacity_gb,
                usedCapacityGb: infra.storage_pool.storage_pool_used_capacity_gb,
                osdTotalCount: infra.storage_pool.osd_total_count,
                osdActiveCount: infra.storage_pool.osd_active_count
            },
            // cont
            containerClusterTemplate: {
                totalCount: cont.container_cluster_template.container_cluster_template_total_count,
            },
            containerCluster: {
                totalCount: cont.container_cluster.container_cluster_total_count,
                activeCount: cont.container_cluster.container_cluster_active_count,
            },
            node: {
                totalCount: cont.node.node_total_count,
                activeCount: cont.node.node_active_count,
                cpuTotalCount: cont.node.cpu_total_count,
                cpuUsedCount: Math.ceil(cont.node.cpu_used_count / 1000 / 1000 / 1000),
                memoryTotalCapacityMb: cont.node.memory_total_capacity_mb,
                memoryUsedCapacityMb: cont.node.memory_used_capacity_mb,
                storageTotalCapacityGb: cont.node.storage_total_capacity_gb,
                storageUsedCapacityGb: cont.node.storage_used_capacity_gb
            },
            registry: {
                totalCount: cont.registry.registry_total_count,
            },
            namespace: {
                totalCount: cont.namespace.namespace_total_count,
            },
            containerImage: {
                totalCount: cont.container_image.container_image_total_count,
            },
            podProfile: {
                totalCount: cont.pod_profile.pod_profile_total_count
            },
            deployment: {
                totalCount: cont.deployment.deployment_total_count,
                activeCount: cont.deployment.deployment_active_count,
            },
            pod: {
                totalCount: cont.pod.pod_total_count,
                activeCount: cont.pod.pod_active_count,
            },
            pvc: {
                totalCount: cont.persistent_volume_claim.persistent_volume_claim_total_count,
                activeCount: cont.persistent_volume_claim.persistent_volume_claim_active_count,
                volumeTotalSizeGb: cont.persistent_volume_claim.persistent_volume_claim_total_size_gb,
            },
            service: {
                totalCount: cont.service.service_total_count,
                clusterIpCount: cont.service.service_type_cluster_ip_count,
                nodePortCount: cont.service.service_type_node_port_count,
                loadBalancerCount: cont.service.service_type_lb_count,
            },
            ingress: {
                totalCount: cont.ingress.ingress_total_count
            },
            // virt
            network: {
                totalCount: virt.network.network_total_count,
                activeCount: virt.network.network_active_count,
                externalCount: virt.network.network_external_count,
            },
            subnet: {
                totalCount: virt.subnet.subnet_total_count
            },
            securityGroup: {
                totalCount: virt.security_group.security_group_total_count,
            },
            securityGroupRule: {
                totalCount: virt.security_group_rule.security_group_rule_total_count
            },
            floatingIp: {
                totalCount: virt.floating_ip.floating_ip_total_count,
                activeCount: virt.floating_ip.floating_ip_active_count,
                attachedCount: virt.floating_ip.floating_ip_attached_count,
            },
            vmImage: {
                totalCount: virt.vm_image.vm_image_total_count,
                activeCount: virt.vm_image.vm_image_active_count,
            },
            vmType: {
                totalCount: virt.vm_type.vm_type_total_count,
            },
            vm: {
                totalCount: virt.vm.vm_total_count,
                activeCount: virt.vm.vm_active_count,
            },
            volume: {
                totalCount: virt.volume.volume_total_count,
                availableCount: virt.volume.volume_available_count,
                attachedCount: virt.volume.volume_attached_count,
                volumeTotalSizeGb: virt.volume.volume_total_size_gb,
            },
            volumeBackup: {
                totalCount: virt.vm_backup.vm_backup_total_count,
            },
            keyPair: {
                totalCount: virt.vm_key_pair.vm_key_pair_total_count
            }
        });
    }



    function parseDashboardProject(auth,virt) {
        setDashboardProject({
            user: {
                maxCount: auth.user.user_max_count,
                totalCount: auth.user.user_total_count,
                activeCount: auth.user.user_active_count,
            },
            network: {
                maxCount: virt.network.network_max_count,
                totalCount: virt.network.network_total_count,
                activeCount: virt.network.network_active_count,
                externalCount: virt.network.network_external_count,
            },
            subnet: {
                maxCount: virt.subnet.subnet_max_count,
                totalCount: virt.subnet.subnet_total_count
            },
            securityGroup: {
                maxCount: virt.security_group.security_group_max_count,
                totalCount: virt.security_group.security_group_total_count,
            },
            securityGroupRule: {
                maxCount: virt.security_group_rule.security_group_rule_max_count,
                totalCount: virt.security_group_rule.security_group_rule_total_count
            },
            floatingIp: {
                maxCount: virt.floating_ip.floating_ip_max_count,
                totalCount: virt.floating_ip.floating_ip_total_count,
                activeCount: virt.floating_ip.floating_ip_active_count,
                attachedCount: virt.floating_ip.floating_ip_attached_count,
            },
            vmImage: {
                maxCount: virt.vm_image.vm_image_max_count,
                totalCount: virt.vm_image.vm_image_total_count,
                activeCount: virt.vm_image.vm_image_active_count,
            },
            vm: {
                maxCount: virt.vm.vm_max_count,
                totalCount: virt.vm.vm_total_count,
                activeCount: virt.vm.vm_active_count,
                cpuMaxCount: virt.vm.vcpu_max_count,
                cpuTotalCount: virt.vm.vcpu_total_count,
                memoryMaxCapacityGb: virt.vm.memory_max_capacity_mb,
                memoryTotalCapacityGb: virt.vm.memory_total_capacity_mb,
                gpuMaxCount: virt.vm.gpu_max_count,
                gpuTotalCount: virt.vm.gpu_total_count,
            },
            volume: {
                maxCount: virt.volume.volume_max_count,
                totalCount: virt.volume.volume_total_count,
                availableCount: virt.volume.volume_available_count,
                attachedCount: virt.volume.volume_attached_count,
                volumeMaxSizeGb: virt.volume.volume_max_size_gb,
                volumeTotalSizeGb: virt.volume.volume_total_size_gb,
            },
            volumeBackup: {
                maxCount: virt.vm_backup.vm_backup_max_count,
                totalCount: virt.vm_backup.vm_backup_total_count,
            },
            keyPair: {
                maxCount: virt.vm_key_pair.vm_key_pair_max_count,
                totalCount: virt.vm_key_pair.vm_key_pair_total_count
            }
        });
    }

    function parseDashboardContainer(cont) {
        setDashboardContainer({
            containerClusterTemplate: {
                totalCount: cont.container_cluster_template.container_cluster_template_total_count,
            },
            containerCluster: {
                totalCount: cont.container_cluster.container_cluster_total_count,
                activeCount: cont.container_cluster.container_cluster_active_count,
            },
            node: {
                totalCount: cont.node.node_total_count,
                activeCount: cont.node.node_active_count,
                cpuTotalCount: cont.node.cpu_total_count,
                cpuUsedCount: Math.ceil(cont.node.cpu_used_count / 1000 / 1000 / 1000),
                memoryTotalCapacityMb: cont.node.memory_total_capacity_mb,
                memoryUsedCapacityMb: cont.node.memory_used_capacity_mb,
                storageTotalCapacityGb: cont.node.storage_total_capacity_gb,
                storageUsedCapacityGb: cont.node.storage_used_capacity_gb
            },
            registry: {
                totalCount: cont.registry.registry_total_count,
            },
            namespace: {
                totalCount: cont.namespace.namespace_total_count,
            },
            containerImage: {
                totalCount: cont.container_image.container_image_total_count,
            },
            podProfile: {
                totalCount: cont.pod_profile.pod_profile_total_count
            },
            deployment: {
                totalCount: cont.deployment.deployment_total_count,
                activeCount: cont.deployment.deployment_active_count,
            },
            pod: {
                totalCount: cont.pod.pod_total_count,
                activeCount: cont.pod.pod_active_count,
            },
            pvc: {
                totalCount: cont.persistent_volume_claim.persistent_volume_claim_total_count,
                activeCount: cont.persistent_volume_claim.persistent_volume_claim_active_count,
                volumeTotalSizeGb: cont.persistent_volume_claim.persistent_volume_claim_total_size_gb,
            },
            service: {
                totalCount: cont.service.service_total_count,
                clusterIpCount: cont.service.service_type_cluster_ip_count,
                nodePortCount: cont.service.service_type_node_port_count,
                loadBalancerCount: cont.service.service_type_lb_count,
            },
            ingress: {
                totalCount: cont.ingress.ingress_total_count
            }
        });
    }


    useInterval(() => {
        viewType && loadDashboard(viewType);
    }, 1000 * 60 * 5)


    function loadDashboard(viewType) {
        if(viewType === 'Infra View') {
            Promise.all([
                requests.get("/auth" + PLATFORM_URL.DASHBOARD),
                requests.get("/infra" + PLATFORM_URL.DASHBOARD),
                requests.get("/virt" + PLATFORM_URL.DASHBOARD),
                requests.get("/cont" + PLATFORM_URL.DASHBOARD)
              ])
            .then(([dataAuth, dataInfra, dataVirt, dataCont]) => {
                parseDashboardInfra(dataAuth.data, dataInfra.data, dataVirt.data, dataCont.data);
                props.setLoading(false);
            })
            .catch((error)=>{
                props.setLoading(false);
            });
        } else if(viewType === 'Project View') {
            Promise.all([
                requests.get("/auth" + PLATFORM_URL.DASHBOARD + "/project"),
                requests.get("/virt" + PLATFORM_URL.DASHBOARD + "/project"),
              ])
            .then(([dataAuth, dataVirt]) => {
                parseDashboardProject(dataAuth.data, dataVirt.data);
                props.setLoading(false);
            })
            .catch((error)=>{
                props.setLoading(false);
            });
        } else if(viewType === 'Container View') {
            Promise.all([
                requests.get("/cont" + PLATFORM_URL.DASHBOARD + "/project"),
              ])
            .then(([dataCont]) => {
                parseDashboardContainer(dataCont.data);
                props.setLoading(false);
            })
            .catch((error)=>{
                props.setLoading(false);
            });
        }
    }

    function changeViewType(viewType) {
        props.setLoading(true);
        loadDashboard(viewType);
        setViewType(viewType);
    }

    return (
        <Dashboard
            dashboardInfra={dashboardInfra}
            dashboardProject={dashboardProject}
            dashboardContainer={dashboardContainer}
            changeViewFunction={changeViewType}
        />
    )
}
